import { useQuery } from '@tanstack/react-query';
import type { TViewedOfficeScope } from '~/types/ViewedOfficeScope';
import { getViewedOfficeId } from '~/utils/viewedOfficeScope';
import { getInvoices } from '../clients/GetInvoices';

const invoicesKeys = {
  all: ['invoices'] as const,
  hasAny: (scope: TViewedOfficeScope) =>
    [...invoicesKeys.all, 'hasAny', scope.kind, getViewedOfficeId(scope) ?? null] as const,
};

/**
 * Whether the scope has any invoice — the rule for showing an Invoices page
 * (GOTU-15554: "if the office has a record of invoices, they will have an Invoice
 * page"). Asks for one row and reads whether it came back, cached per scope:
 * `GET /invoices` answers `total: 0` when `limit` is 1 even with invoices on the
 * page, so the total cannot decide it. `false` until it answers, and while `scope`
 * is unknown.
 */
export const useHasInvoices = (scope: TViewedOfficeScope | undefined): boolean => {
  const { data } = useQuery({
    queryKey: invoicesKeys.hasAny(scope ?? { kind: 'selected' }),
    queryFn: () => getInvoices({ page: 1, limit: 1 }, scope as TViewedOfficeScope),
    enabled: !!scope,
    select: (response) => response.invoices.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return data ?? false;
};
